import { useEffect, useState } from "react";
import { getRooms, getTickets, getPayments, sendBulkReminder } from "../../firebase/services";
import { Badge, Card, SectionTitle, Spinner } from "../../components/UI";
import IamLogo from "../../components/IamLogo";

const LOYER = 27500;
const MOIS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

function formatMonthValue(val) {
  if (!val) return "";
  try {
    const [year, month] = val.split("-");
    return `${MOIS[parseInt(month) - 1]} ${year}`;
  } catch(e) { return val; }
}

function getMonthKey(p) {
  if (p.month) return p.month;
  if (p.createdAt) {
    const d = p.createdAt.toDate ? p.createdAt.toDate() : new Date(p.createdAt);
    return d.toISOString().slice(0, 7);
  }
  return "0000-00";
}

function groupConfirmedByMonth(payments) {
  const groups = {};
  payments.filter(p => p.status === "confirmed").forEach(p => {
    const key = getMonthKey(p);
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  });
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
}

export default function OwnerDashboard({ onNavigate }) {
  const [rooms, setRooms]       = useState([]);
  const [tickets, setTickets]   = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const [notifSent, setNotifSent] = useState(false);
  const [openMonth, setOpenMonth] = useState(null);

  useEffect(() => {
    const unsubs = [
      getRooms(data => { setRooms(data); setLoading(false); }),
      getTickets(setTickets),
      getPayments(setPayments),
    ];
    return () => unsubs.forEach(u => u());
  }, []);

  // Ouvrir le mois le plus récent automatiquement
  useEffect(() => {
    if (payments.length > 0 && openMonth === null) {
      const groups = groupConfirmedByMonth(payments);
      if (groups.length > 0) setOpenMonth(groups[0][0]);
    }
  }, [payments, openMonth]);

  const openTickets    = tickets.filter(t => t.status === "open");
  const progTickets    = tickets.filter(t => t.status === "prog");
  const lateRooms      = rooms.filter(r => r.paymentStatus === "late");
  const pendingPayments = payments.filter(p => p.status === "pending");
  const confirmedGroups   = groupConfirmedByMonth(payments);
  const currentMonthKey   = new Date().toISOString().slice(0, 7); // "2026-07"
  const currentMonthGroup = confirmedGroups.find(([k]) => k === currentMonthKey)?.[1] || [];
  const nbOccupes         = rooms.filter(r => r.tenantName).length;
  const currentMonthAmt   = currentMonthGroup.length * LOYER;
  const currentMonthLabel = formatMonthValue(currentMonthKey);
  const cumulTotal        = payments.filter(p => p.status === "confirmed").length * LOYER;

  const roomName = (id) => {
    const r = rooms.find(r => r.id === id);
    return r ? `Ch. ${r.number}${r.tenantName ? ` — ${r.tenantName}` : ""}` : "?";
  };

  const handleSendReminders = async () => {
    setSending(true);
    const overdueRooms = rooms.filter(r => ["late","due_soon"].includes(r.paymentStatus));
    await sendBulkReminder(overdueRooms);
    setSending(false);
    setNotifSent(true);
    setTimeout(() => setNotifSent(false), 3000);
  };

  if (loading) return (
    <div style={S.page}>
      <div style={S.header}><IamLogo size={36} /></div>
      <Spinner />
    </div>
  );

  return (
    <div style={S.page}>
      <div style={S.header}>
        <IamLogo size={36} />
        <button onClick={() => onNavigate("notifications")} style={S.iconBtn} aria-label="Notifications">
          <span style={{ fontSize: 20 }}>🔔</span>
          {(openTickets.length + pendingPayments.length) > 0 && (
            <span style={S.notifDot}>{openTickets.length + pendingPayments.length}</span>
          )}
        </button>
      </div>

      <div style={S.scroll}>
        {/* Stats */}
        <div style={S.statsGrid}>
          <div style={S.statBox}>
            <span style={S.statNum}>{rooms.length}</span>
            <span style={S.statLabel}>Chambres</span>
          </div>
          <div style={{ ...S.statBox, ...(openTickets.length > 0 ? S.statAlert : {}) }}>
            <span style={{ ...S.statNum, color: openTickets.length > 0 ? "#a32d2d" : "#1a3a5c" }}>
              {openTickets.length + progTickets.length}
            </span>
            <span style={S.statLabel}>Tickets actifs</span>
          </div>
          <div style={{ ...S.statBox, ...(lateRooms.length > 0 ? S.statWarn : {}) }}>
            <span style={{ ...S.statNum, color: lateRooms.length > 0 ? "#8a4c00" : "#1a3a5c" }}>
              {lateRooms.length}
            </span>
            <span style={S.statLabel}>Retards</span>
          </div>
        </div>

        {/* ── Bloc loyers par mois ── */}
        <div style={S.revenueBlock}>
          {/* En-tête : mois en cours */}
          <div style={S.revenueHeader}>
            <div>
              <p style={S.revHeaderSub}>Loyers confirmés — {currentMonthLabel}</p>
              <p style={S.revHeaderAmt}>{currentMonthAmt.toLocaleString("fr-FR")} FCFA</p>
              <p style={S.revHeaderSub}>
                {currentMonthGroup.length} / {nbOccupes} chambres · sur {(nbOccupes * LOYER).toLocaleString("fr-FR")} FCFA attendus
              </p>
            </div>
            <div style={{ fontSize: 32 }}>💰</div>
          </div>

          {/* Accordéon par mois */}
          <div style={S.monthList}>
            {confirmedGroups.length === 0 && (
              <p style={{ fontSize:13, color:"#90bde0", textAlign:"center", padding:"12px 0" }}>Aucun loyer confirmé</p>
            )}
            {confirmedGroups.map(([monthKey, group]) => {
              const isOpen = openMonth === monthKey;
              const label  = formatMonthValue(monthKey);
              const total  = group.length * LOYER;
              const nbOccupes = rooms.filter(r => r.tenantName).length;
              return (
                <div key={monthKey} style={S.monthGroup}>
                  {/* En-tête du mois */}
                  <button
                    style={S.monthHeader}
                    onClick={() => setOpenMonth(isOpen ? null : monthKey)}
                  >
                    <div>
                      <span style={S.monthTitle}>{label}</span>
                      <span style={S.monthSub}>{total.toLocaleString("fr-FR")} FCFA</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={S.monthBadge}>{group.length} / {nbOccupes}</span>
                      <span style={{ color:"#90bde0", fontSize:16, transition:"transform .2s", display:"inline-block", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>⌄</span>
                    </div>
                  </button>

                  {/* Détail chambres */}
                  {isOpen && (
                    <div style={S.monthBody}>
                      {group
                        .sort((a,b) => (a.roomId||"").localeCompare(b.roomId||""))
                        .map(p => (
                          <div key={p.id} style={S.payRow}>
                            <span style={S.payRoomName}>{roomName(p.roomId)}</span>
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <span style={S.payMethod}>{p.method || "—"}</span>
                              <span style={S.payAmt}>{LOYER.toLocaleString("fr-FR")} F</span>
                              <span style={S.payCheck}>✓</span>
                            </div>
                          </div>
                        ))
                      }
                      {/* Chambres non payées ce mois */}
                      {rooms.filter(r => r.tenantName && !group.find(p => p.roomId === r.id)).map(r => (
                        <div key={r.id} style={{ ...S.payRow, opacity:0.5 }}>
                          <span style={S.payRoomName}>Ch. {r.number} — {r.tenantName}</span>
                          <span style={{ fontSize:11, color:"#f5b8b8" }}>Non payé</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Cumul total en bas */}
          <div style={S.cumulRow}>
            <span style={S.cumulLabel}>📊 Cumul total perçu depuis le début</span>
            <span style={S.cumulAmt}>{cumulTotal.toLocaleString("fr-FR")} FCFA</span>
          </div>
        </div>

        {/* Tickets ouverts */}
        {openTickets.length > 0 && (
          <>
            <SectionTitle>Tickets ouverts</SectionTitle>
            {openTickets.map(t => (
              <div key={t.id} style={S.alertCard} onClick={() => onNavigate("tickets")}>
                <span style={{ fontSize: 18 }}>{categoryIcon(t.category)}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: "#1a2a3a", margin: 0 }}>
                    {t.description?.slice(0, 50)}…
                  </p>
                  <p style={{ fontSize: 11, color: "#7a90a8", margin: "2px 0 0" }}>
                    Ch. {rooms.find(r => r.id === t.roomId)?.number} · {t.category} · {timeAgo(t.createdAt)}
                  </p>
                </div>
                <Badge type="open">Ouvert</Badge>
              </div>
            ))}
          </>
        )}

        {/* Paiements à confirmer */}
        {pendingPayments.length > 0 && (
          <>
            <SectionTitle>Paiements à confirmer</SectionTitle>
            {pendingPayments.map(p => (
              <div key={p.id} style={S.alertCard} onClick={() => onNavigate("payments")}>
                <span style={{ fontSize: 18 }}>💳</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: "#1a2a3a", margin: 0 }}>
                    Ch. {rooms.find(r => r.id === p.roomId)?.number} — 27 500 FCFA
                  </p>
                  <p style={{ fontSize: 11, color: "#7a90a8", margin: "2px 0 0" }}>
                    {p.method} · réf: {p.reference}
                  </p>
                </div>
                <Badge type="pend">À confirmer</Badge>
              </div>
            ))}
          </>
        )}

        {/* Actions rapides */}
        <SectionTitle>Actions rapides</SectionTitle>
        <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            style={{ ...S.actionBtn, background: "#1a3a5c", color: "white" }}
            onClick={handleSendReminders}
            disabled={sending}
          >
            <span>🔔</span>
            {sending ? "Envoi en cours..." : notifSent ? "✓ Rappels envoyés !" : "Envoyer rappels de paiement"}
          </button>
          <button
            style={{ ...S.actionBtn, background: "#f0f5fa", color: "#1a3a5c" }}
            onClick={() => onNavigate("rooms")}
          >
            <span>🏠</span> Voir toutes les chambres
          </button>
        </div>
      </div>
    </div>
  );
}

function categoryIcon(cat) {
  const icons = { Plomberie:"🔧", "Électricité":"⚡", "Serrure / porte":"🔑", Climatisation:"❄️", Autre:"⚙️" };
  return icons[cat] || "⚙️";
}

function timeAgo(ts) {
  if (!ts) return "";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.round(diff / 3600)}h`;
  return `il y a ${Math.round(diff / 86400)}j`;
}

const S = {
  page:       { display:"flex", flexDirection:"column", height:"100%", background:"#f5f8fc", fontFamily:"'Barlow',sans-serif" },
  header:     { background:"#1a3a5c", padding:"14px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" },
  iconBtn:    { background:"none", border:"none", cursor:"pointer", position:"relative", padding:4 },
  notifDot:   { position:"absolute", top:0, right:0, width:16, height:16, background:"#e24b4a", borderRadius:"50%", fontSize:9, color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700 },
  scroll:     { flex:1, overflowY:"auto", paddingBottom:80 },
  statsGrid:  { display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, padding:16 },
  statBox:    { background:"white", borderRadius:12, padding:"12px 8px", textAlign:"center", border:"0.5px solid #d8e8f4" },
  statAlert:  { border:"1px solid #f5b8b8", background:"#fff5f5" },
  statWarn:   { border:"1px solid #f5d8a0", background:"#fffaf0" },
  statNum:    { display:"block", fontSize:22, fontWeight:800, color:"#1a3a5c", fontFamily:"'Barlow Condensed',sans-serif" },
  statLabel:  { display:"block", fontSize:10, color:"#7a90a8", marginTop:2, fontWeight:500 },

  // Bloc loyers
  revenueBlock:  { margin:"0 16px 16px", background:"linear-gradient(135deg,#1a3a5c 0%,#2e6aa0 100%)", borderRadius:16, overflow:"hidden" },
  revenueHeader: { padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" },
  revHeaderSub:  { fontSize:12, color:"#90bde0", margin:"0 0 2px" },
  revHeaderAmt:  { fontSize:24, fontWeight:800, color:"white", fontFamily:"'Barlow Condensed',sans-serif", margin:"0 0 2px" },

  monthList:   { borderTop:"0.5px solid rgba(255,255,255,0.15)" },
  monthGroup:  {},
  monthHeader: { width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", background:"transparent", border:"none", borderBottom:"0.5px solid rgba(255,255,255,0.1)", padding:"11px 20px", cursor:"pointer", fontFamily:"inherit", textAlign:"left", boxSizing:"border-box" },
  monthTitle:  { display:"block", fontSize:14, fontWeight:700, color:"white", fontFamily:"'Barlow Condensed',sans-serif", textTransform:"capitalize" },
  monthSub:    { display:"block", fontSize:11, color:"#90bde0", marginTop:1 },
  monthBadge:  { background:"rgba(255,255,255,0.15)", color:"white", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700 },
  monthBody:   { background:"rgba(0,0,0,0.15)", padding:"4px 0 4px" },
  payRow:      { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 20px", borderBottom:"0.5px solid rgba(255,255,255,0.06)" },
  payRoomName: { fontSize:12, color:"white", fontWeight:600 },
  payMethod:   { fontSize:11, color:"#90bde0" },
  payAmt:      { fontSize:12, color:"white", fontWeight:700, fontFamily:"'Barlow Condensed',sans-serif" },
  payCheck:    { fontSize:13, color:"#7ed957" },

  cumulRow:    { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 20px", borderTop:"0.5px solid rgba(255,255,255,0.15)", background:"rgba(0,0,0,0.15)" },
  cumulLabel:  { fontSize:11, color:"#90bde0" },
  cumulAmt:    { fontSize:13, fontWeight:800, color:"white", fontFamily:"'Barlow Condensed',sans-serif" },
  alertCard:  { margin:"0 16px 8px", background:"white", borderRadius:12, padding:"12px 14px", display:"flex", alignItems:"center", gap:10, border:"0.5px solid #d8e8f4", cursor:"pointer" },
  actionBtn:  { display:"flex", alignItems:"center", gap:10, padding:"13px 16px", borderRadius:12, border:"none", fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"inherit" },
};
